/**
 * /shares — owner + recipient deal-link inbox.
 *
 * `external-sharing` Phase 3 Session 3.1 (route stub) + Session 3.3 (Sent tab body).
 * Phase 4 Session 4.1 fills the Received tab.
 *
 * URL search: `?tab=received|sent` (default `received` per spec §6) +
 * optional `?highlight=<uuid>` carried by `<HeaderShareBell>` click navigation.
 */
import { z } from 'zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { ReceivedTab } from '@/components/shares/ReceivedTab';
import { SentTab } from '@/components/shares/SentTab';

export const sharesSearchSchema = z.object({
  tab: z.enum(['received', 'sent']).catch('received').default('received'),
  highlight: z.string().uuid().optional().catch(undefined),
});

export type SharesSearch = z.infer<typeof sharesSearchSchema>;

const TABS: Array<{ id: 'received' | 'sent'; label: string }> = [
  { id: 'received', label: 'Received' },
  { id: 'sent', label: 'Sent' },
];

export function SharesPage() {
  const search = useSearch({ from: '/shares' }) as SharesSearch;
  const navigate = useNavigate();

  const handleTabChange = (tab: 'received' | 'sent'): void => {
    // /shares only has 2 search params (`tab`, `highlight`) — explicitly
    // reconstructing the search object preserves `highlight` while flipping
    // `tab`. The functional-setter form yields a router-wide union type for
    // `prev` that doesn't narrow to SharesSearch, so we pass the object form.
    void navigate({
      to: '/shares',
      search: { tab, highlight: search.highlight },
      replace: true,
    });
  };

  return (
    <div className="h-full overflow-auto bg-bg p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-base font-semibold text-text-bright font-mono tracking-[3px] mb-1">
            DEAL LINKS
          </h1>
          <p className="text-sm text-text-muted font-body">
            Shares you&apos;ve sent and received.
          </p>
        </div>

        <div className="flex items-center gap-1 border-b border-panel-border mb-6">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={search.tab === t.id}
              data-testid={`shares-tab-${t.id}`}
              onClick={() => handleTabChange(t.id)}
              className={clsx(
                'px-4 py-2 -mb-px border-b-2 cursor-pointer',
                'text-xs font-mono font-semibold uppercase tracking-[1.2px]',
                'transition-colors duration-150',
                search.tab === t.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-text-muted hover:text-text-bright',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div data-testid="shares-tab-content" data-active-tab={search.tab}>
          {search.tab === 'sent' ? (
            <SentTab highlightId={search.highlight} />
          ) : (
            <ReceivedTab highlightId={search.highlight} />
          )}
        </div>
      </div>
    </div>
  );
}
